import { Appointment } from "@/Common/Api/Appointment/Dto/Appointment";
import { Contact } from "@/Common/Api/Contacts/Dto/Contact";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { EmployeeGroup } from "@/Common/Api/Employee/Dto/EmployeeGroup";
import { EmployeeSearch } from "@/Common/Api/Employee/Dto/EmployeeSearch";
import { Individual } from "@/Common/Api/Individuals/Dto/Individual";
import { IndividualLight } from "@/Common/Api/Individuals/Dto/IndividualLight";
import { LoadIndividualResponse } from "@/Common/Api/Individuals/Dto/LoadIndividualResponse";
import { SearchIndividualResult } from "@/Common/Api/Individuals/Dto/SearchIndividualResult";
import { LicencedIntervenor } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicencedIntervenor";
import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { PhoneNumber } from "@/Common/Models/Contacts/PhoneNumber";
import { Guid } from "@/Common/Models/Guid";
import { ContactFacetType } from "@/Common/Models/Individuals/Enums/ContactFacetType";
import { GenderIdentity } from "@/Common/Models/Individuals/Enums/GenderIdentity";
import { PreferredPronouns } from "@/Common/Models/Individuals/Enums/PreferredPronouns";
import { Patient as MyleModelsPatientPatient } from "@/Common/Models/Patient/Patient";
import { Gender } from "@/Common/Models/Patient/PatientEnums";
import { PatientLight } from "@/Common/Models/Patient/PatientLight";
import { PatientProfileMyleDto } from "@/Common/Models/Portal/Patient/SharedPatientMyleDto";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { PortalRegistrationState } from "../Portal/Enums/PortalRegistrationState";
import { BasePerson } from "./BasePerson";

class Person extends BasePerson {
    private _nameOverride: string;

    /* Ids */
    public ContactId: Guid;
    public ContactFacetId: Guid;
    public ContactFacetType: ContactFacetType;

    /* Common Fields */
    public FirstName: string;
    public LastName: string;
    public PreferredName: string;
    public PreferredPronouns: PreferredPronouns;
    public SpecifiedPreferredPronouns: string;
    public GenderIdentity: GenderIdentity;

    public FileNumber: string;
    public Identifier01: string;

    public NAM: string;
    public FormattedNAM: string;
    public Title: string;
    public Identifier: string;
    public DateofBirth: Date;
    public PreferedPhone: string;
    public IsPersonGroup: boolean;
    public Licence: string;
    public OrganizationRuleId: string;
    public RamqNumber: string;
    public PortalSyncState: number;
    public PortalProfileId: string;
    public PrefixLookupId: string;
    public GenderLookupId: string;
    public prefix: string = "";
    public fatherFirstName = "";
    public fatherLastName = "";
    public motherFirstName = "";
    public motherLastName = "";
    public gender: Gender = Gender.Unspecified;
    public isSupervisorRequired: boolean = false;
    public IsInactive: boolean = false;
    public SourceContext: PersonSourceContext = null;
    public EnrollingPhysicianLicence: string;
    public EnrollingDelegateLicence: string;

    /** Last update date of the memo note. */
    public memoLastModifiedDate?: Date;

    /** Memo note of the person file. */
    public memoNote?: string;

    /** Employee ID that last modify the memo note. */
    public lastModifiedMemoEmployeeId?: Guid;

    /** Deactivation note. */
    public inactivationNote?: string;

    /** Is person active. */
    public isInactive?: boolean;

    /** Reason lookup ID for deactivation . */
    public reasonForInactivityLookupId?: string;

    /**
         * Whether this model was initialized via {@link FromPatientLight}
         * so it only has basic info like the name and IDs.
         */
    public isLightModel: boolean = false;

    public IsReadOnly?: boolean;

    // pl says: that shit ain't right yo
    /* Base Models */
    public EmployeeGroupModel: EmployeeGroup;
    public GroupEmployeeModels: Guid[]; // Array of Employee ContactFacetId
    public EmployeeModel: Employee;
    public Patient: MyleModelsPatientPatient;
    public PortalPatient: PatientProfileMyleDto;
    public EmployeeSearchModel: EmployeeSearch;

    constructor(fields?: Partial<Person>) {
        super();
        this.ContactId = null;
        this.ContactFacetId = null;
        this.PreferedPhone = "";
        Object.assign(this, fields);
    }

    public get DateOfBirth(): Date {
        return this.DateofBirth;
    }

    public set DateOfBirth(dob: Date) {
        this.DateofBirth = dob;
    }

    /**
     * Get the person's full name in `last name, first name` form.
     */
    public get Name(): string {
        return this._nameOverride
            || NameHelper.formatFullName(this.FirstName, this.LastName, this.PreferredName);
    }

    /**
     * Set the person's full name directly, instead of formatting it automatically from the first/last name. This should be avoided in most cases to keep the format consistent.
     * @param name The name to set.
     */
    public set Name(name: string) {
        this._nameOverride = name;
    }

    /**
     * For a patient, the full name in `last name, first name` form with a parenthetical identifier if available (e.g. NAM).
     * For an employee, the full name with title.
     */
    public get NameWithIdentifier(): string {
        // group
        if (this.IsPersonGroup)
            return this.Name;

        // employee
        if (this.EmployeeModel)
            return this.NameWithPrefix;

        // patient
        let id = this.NAM ? this.GetFormattedNAM() : this.Identifier;
        return id
            ? `${this.Name} (${id})`
            : this.Name;
    }

    /**
     * The full name with title prefix, if any.
     */
    public get NameWithPrefix(): string {
        return this.prefix
            ? `${this.Name}, ${this.prefix}` // the name is Last, First - prefix should go last to preserve sorting
            : `${this.Name}`;
    }

    /**
     * The full name with title prefix FirstName LastName.
     */
    public NameWithPrefixFirst(): string {
        return this.prefix
            ? `${this.prefix} ${this.FirstName} ${this.LastName}`
            : `${this.FirstName} ${this.LastName}`;
    }

    public ToSearchableItem(includeModels: boolean): SearchableItemModelT<Person> {
        return new SearchableItemModelT<Person>(this.NameWithPrefix, this.ContactId, includeModels ? this : null);
    }

    public static FromEmployeeSearch(emp: EmployeeSearch): Person {
        const person = new Person();
        person.EmployeeSearchModel = emp;
        person.ContactId = emp.Contact_id;
        person.ContactFacetId = emp.Inactivable_id;
        person.FirstName = emp.FirstName;
        person.LastName = emp.LastName;
        person.PrefixLookupId = emp.PrefixLookupId;

        if (emp.PrefixLookupId)
            person.prefix = cacheStore.GetLocalizedValueByLookupId(emp.PrefixLookupId);

        person.Title = "";
        if (emp.IssuingLicenseOrganizationRuleId) {
            const title = LicenceOrganisationRule.getTitleName(cacheStore.LicenceOrganisationRulesById[emp.IssuingLicenseOrganizationRuleId]);
            if (title)
                person.Title = title;
        }

        return person;
    }

    public static FromPatient(patient: MyleModelsPatientPatient): Person | null {
        if (!patient)
            return null;

        const person = new Person();
        person.Patient = patient;

        person.ContactFacetId = patient.PatientResponse.ContactFacet_id;
        person.ContactFacetType = ContactFacetType.Patient;
        person.ContactId = patient.IndividualResponse.Contact_id;

        person.FirstName = patient.IndividualResponse.FirstName;
        person.LastName = patient.IndividualResponse.LastName;
        person.PreferredName = patient.IndividualResponse.PreferredName;
        person.PreferredPronouns = patient.IndividualResponse.PreferredPronouns;
        person.SpecifiedPreferredPronouns = patient.IndividualResponse.SpecifiedPreferredPronouns;
        person.DateofBirth = patient.IndividualResponse.DateOfBirth && new DateOnly(patient.IndividualResponse.DateOfBirth).toJSDate();
        person.GenderLookupId = patient.IndividualResponse.GenderLookupId;
        person.GenderIdentity = patient.IndividualResponse.GenderIdentity;

        person.PortalSyncState = patient.IndividualResponse.PortalSyncState;
        person.PortalProfileId = patient.IndividualResponse.PortalProfileId;

        person.fatherLastName = patient.IndividualResponse.FatherLastName;
        person.fatherFirstName = patient.IndividualResponse.FatherFirstName;
        person.motherFirstName = patient.IndividualResponse.MotherFirstName;
        person.motherLastName = patient.IndividualResponse.MotherLastName;
        person.gender = patient.Gender();
        person.PrefixLookupId = patient.IndividualResponse.PrefixLookupId;

        if (patient.IndividualResponse.PrefixLookupId)
            person.prefix = cacheStore.GetLocalizedValueByLookupId(patient.IndividualResponse.PrefixLookupId);

        person.NAM = patient.IndividualResponse.NAM || null;
        person.Identifier = patient.GetIdentifiers().firstOrDefault();
        if (!person.Identifier && patient.IndividualResponse && !IsNullOrWhiteSpace(patient.IndividualResponse.FileNumber))
            person.Identifier = patient.IndividualResponse.FileNumber;

        person.PreferedPhone = patient.Phones?.firstOrDefault()?.Number ?? "";
        person.memoLastModifiedDate = patient.IndividualResponse.MemoLastModifiedDate;
        person.memoNote = patient.IndividualResponse.MemoNote;
        person.lastModifiedMemoEmployeeId = patient.IndividualResponse.LastModifiedMemoEmployeeId;
        person.inactivationNote = patient.InactivableResponse && patient.InactivableResponse.InactivationNote;
        person.isInactive = patient.InactivableResponse && patient.InactivableResponse.IsInactive;
        person.reasonForInactivityLookupId = patient.InactivableResponse && patient.InactivableResponse.ReasonForInactivityLookupId;

        // Contact
        person.ContactPoints = patient.ContactPoints;
        person.Phones = patient.Phones;
        person.Addresses = patient.Addresses;
        person.Emails = patient.Emails;
        person.Relationships = patient.Relationships;
        person.IsReadOnly = patient.IsReadOnly;

        return person;
    }

    public static FromLoadAppointment(appointment: Appointment): Person {
        return Person.FromPatient(MyleModelsPatientPatient.FromLoadAppointment(appointment));
    }

    public GetFormattedNAM(): string {
        if (!IsNullOrWhiteSpace(this.NAM))
            return this.NAM;
        else if (this.Patient)
            return this.Patient.GetFormattedNAM(); // Include RepresentantNAM
        else
            return "";
    }

    public GetNameWithNam(): string {
        return this.Name + (this.NAM ? " - " + this.NAM : "");
    }

    public get formattedNameLicencePreferredPhone(): string {
        let output = this.Name;
        const trimmedLicense = this.Licence?.trim();
        const trimmedPhone = this.PreferedPhone?.trim();

        if (trimmedLicense)
            output += ` (${trimmedLicense})`;

        if (trimmedPhone)
            output += ` - ${trimmedPhone}`;

        return output;
    }

    public static FromLinkedContact(linkedContact: LinkedContact): Person {
        if (!linkedContact.TargetContactId)
            return null;

        const person = Person.FromIndividual(linkedContact.Individual);
        person.Addresses = linkedContact.Addresses;
        person.Phones = linkedContact.PhoneNumbers;
        person.ContactPoints = linkedContact.ContactPoints;
        person.Emails = linkedContact.Emails;

        return person;
    }

    public static FromEmployee(emp: Employee, useFormattedLicence: boolean = false, useProfessionalNumberWhenRequired: boolean = false): Person {
        const person = new Person();
        person.EmployeeModel = emp;
        person.ContactId = emp.Contact_id;
        person.ContactFacetId = emp.ContactFacet_id;
        person.FirstName = emp.FirstName;
        person.LastName = emp.LastName;
        person.Licence = useFormattedLicence
            ? Employee.getFormattedLicence(emp, useProfessionalNumberWhenRequired)
            : emp.FirstLicenceNumber;
        person.RamqNumber = emp.RamqNumber;
        person.PrefixLookupId = emp.PrefixLookupId;

        if (emp.PrefixLookupId)
            person.prefix = cacheStore.GetLocalizedValueByLookupId(emp.PrefixLookupId);

        person.isSupervisorRequired = Employee.shouldHaveSupervisorBasedOnTitle(emp);

        person.Title = "";
        if (Employee.getIssuingOrgRule(emp)) {
            const title = LicenceOrganisationRule.getTitleName(Employee.getIssuingOrgRule(emp));
            if (title)
                person.Title = title;
        }

        person.IsInactive = emp.IsInactive;

        return person;
    }

    public static FromPatientLight(patientLight: PatientLight): Person {
        const person = new Person();
        person.ContactId = patientLight.ContactId;
        person.ContactFacetId = patientLight.ContactFacetId;
        person.FirstName = patientLight.FirstName;
        person.LastName = patientLight.LastName;
        person.PreferredName = patientLight.PreferredName;
        person.PreferredPronouns = patientLight.PreferredPronouns;
        person.SpecifiedPreferredPronouns = patientLight.SpecifiedPreferredPronouns;
        person.GenderIdentity = patientLight.GenderIdentity;
        person.Identifier = patientLight.Identifier;
        person.PortalSyncState = patientLight.PortalSyncState;
        person.PortalProfileId = patientLight.PortalProfileId;
        person.isLightModel = true;

        return person;
    }

    public static FromContact(contact: Contact): Person {
        const person = new Person();
        person.ContactId = contact.Inactivable_id;
        person.FirstName = contact.SearchableSummary_Search1;
        person.LastName = contact.SearchableSummary_Search2;
        person.PreferredName = contact.SearchableSummary_Search14;
        person.DateofBirth = !IsNullOrWhiteSpace(contact.SearchableSummary_Search10) ? new Date(Date.parse(contact.SearchableSummary_Search10)) : null;

        person.NAM = contact.SearchableSummary_Search9;
        return person;
    }

    public static FromIndividualLight(individual: IndividualLight): Person {
        const person = new Person();
        person.ContactId = individual.Id;
        person.FirstName = individual.FirstName;
        person.LastName = individual.LastName;
        person.Identifier = individual.FileNumber;
        person.PrefixLookupId = individual.Prefix;

        if (individual.Prefix)
            person.prefix = cacheStore.GetLocalizedValueByLookupId(individual.Prefix);

        person.NAM = individual.NAM;

        person.PortalSyncState = individual.PortalSyncState;
        person.PortalProfileId = individual.PortalProfileId;
        return person;
    }

    public static ToIndividual(person: Person): Individual {
        const individual = new Individual();
        individual.Contact_id = person.ContactId;
        individual.ContactFacetId = person.ContactFacetId;
        individual.ContactFacetType = person.ContactFacetType;
        individual.DateOfBirth = person.DateOfBirth;
        individual.FirstName = person.FirstName;
        individual.LastName = person.LastName;
        individual.NAM = individual.NAM || null;
        individual.PortalSyncState = person.PortalSyncState;
        individual.PortalProfileId = person.PortalProfileId;

        individual.GenderLookupId = person.GenderLookupId;
        individual.FatherLastName = person.fatherLastName;
        individual.FatherFirstName = person.fatherFirstName;
        individual.MotherFirstName = person.motherFirstName;
        individual.MotherLastName = person.motherLastName;
        individual.PrefixLookupId = person.PrefixLookupId;

        individual.PreferredName = person.PreferredName;
        individual.GenderIdentity = person.GenderIdentity;
        individual.PreferredPronouns = person.PreferredPronouns;
        individual.SpecifiedPreferredPronouns = person.SpecifiedPreferredPronouns;

        return individual;
    }

    public static FromIndividual(individual: Individual): Person {
        const person = new Person();
        person.ContactId = individual.Contact_id;
        person.ContactFacetId = individual.ContactFacetId;
        person.ContactFacetType = individual.ContactFacetType;
        person.DateofBirth = individual.DateOfBirth && new Date(individual.DateOfBirth.toString());
        person.FirstName = individual.FirstName;
        person.LastName = individual.LastName;
        person.PreferredName = individual.PreferredName;
        person.PreferredPronouns = individual.PreferredPronouns;
        person.SpecifiedPreferredPronouns = individual.SpecifiedPreferredPronouns;
        person.GenderIdentity = individual.GenderIdentity;
        person.NAM = individual.NAM;
        person.PortalSyncState = individual.PortalSyncState;
        person.PortalProfileId = individual.PortalProfileId;
        person.FileNumber = individual.FileNumber;
        person.GenderLookupId = individual.GenderLookupId;
        person.fatherLastName = individual.FatherLastName;
        person.fatherFirstName = individual.FatherFirstName;
        person.motherFirstName = individual.MotherFirstName;
        person.motherLastName = individual.MotherLastName;
        person.PrefixLookupId = individual.PrefixLookupId;
        person.Identifier01 = individual.Identifier01;

        if (individual.PrefixLookupId)
            person.prefix = cacheStore.GetLocalizedValueByLookupId(individual.PrefixLookupId);

        return person;
    }

    public static fromLoadIndividual(loadIndividual: LoadIndividualResponse): Person {
        let person: Person = this.FromIndividual(loadIndividual.Individual);
        person.Emails = loadIndividual.Emails?.mutate(p => p.renderId = sessionStore.newId().toString());
        person.Phones = loadIndividual.PhoneNumbers?.mutate(p => p.renderId = sessionStore.newId().toString());
        person.Addresses = loadIndividual.Addresses;
        person.ContactPoints = loadIndividual.ContactPoints;
        person.HealthCards = loadIndividual.HealthCards;
        person.OtherPatientIdentifiers = loadIndividual.OtherPatientIdentifiers;
        person.Relationships = loadIndividual.Relationships;
        person.memoLastModifiedDate = loadIndividual.Individual.MemoLastModifiedDate;
        person.memoNote = loadIndividual.Individual.MemoNote;
        person.lastModifiedMemoEmployeeId = loadIndividual.Individual.LastModifiedMemoEmployeeId;
        person.inactivationNote = loadIndividual.Inactivable.InactivationNote;
        person.isInactive = loadIndividual.Inactivable.IsInactive;
        person.reasonForInactivityLookupId = loadIndividual.Inactivable.ReasonForInactivityLookupId;
        person.IsReadOnly = loadIndividual.Individual.IsReadOnly;

        person.HealthCards?.forEach(healthCard => {
            if (healthCard.ProvinceLookupId) {
                healthCard.CountryLookupId = cacheStore.GetCountryForRegion(healthCard.ProvinceLookupId).LookupID;
            }
        });

        return person;
    }

    public static FromSearchIndividualResult(individualResult: SearchIndividualResult): Person {
        const person = new Person();
        person.ContactId = individualResult.ContactId;
        person.ContactFacetId = individualResult.ContactFacetId;
        person.ContactFacetType = individualResult.ContactFacetType;
        person.FirstName = individualResult.FirstName;
        person.LastName = individualResult.LastName;
        person.Identifier = individualResult.Identifier;
        person.PortalSyncState = individualResult.PortalSyncState;
        person.PortalProfileId = individualResult.PortalProfileId;
        person.PreferredName = individualResult.PreferredName;
        person.isLightModel = true;
        person.EnrollingPhysicianLicence = individualResult.EnrollingPhysicianLicence;
        person.EnrollingDelegateLicence = individualResult.EnrollingDelegateLicence;
        return person;
    }

    public static FromIndividualToDropdownModel(individual: IndividualLight): PersonDropdownItem {
        const person = new Person();
        person.ContactId = individual.Id;
        person.FirstName = individual.FirstName;
        person.LastName = individual.LastName;

        person.NAM = individual.NAM;
        person.PortalSyncState = individual.PortalSyncState;
        person.PortalProfileId = individual.PortalProfileId;

        const dm = new PersonDropdownItem();
        dm.Item = person;
        dm.Active = false;
        return dm;
    }

    public static FromMedfarDictionnaryLicence(doc: LicencedIntervenor): Person {
        const p = new Person();
        p.Licence = doc.LicenceNumber;
        p.FirstName = doc.FirstName;
        p.LastName = doc.LastName;
        p.OrganizationRuleId = doc.LicenceOrganisationRuleId;

        p.PreferedPhone = doc.Phone;

        return p;
    }

    public static FromPortalPatientProfile(portalPatientProfile: PatientProfileMyleDto): Person {
        const p = new Person();
        p.ContactFacetType = ContactFacetType.Patient;
        p.ContactId = portalPatientProfile.MyleId;
        p.FirstName = portalPatientProfile.FirstName;
        p.PreferredName = portalPatientProfile.PreferredName;
        p.LastName = portalPatientProfile.LastName;
        p.gender = MyleHelper.genderFromPortalMedicalSex(portalPatientProfile.Sex);

        if (portalPatientProfile.InsuranceNumber) {
            p.NAM = portalPatientProfile.InsuranceNumber;
            p.Identifier = p.NAM;
        }

        p.PortalSyncState = portalPatientProfile.PortalSyncState;
        p.PortalProfileId = portalPatientProfile.PatientProfileId;
        p.PortalPatient = portalPatientProfile;

        return p;
    }

    /**
     * Return true if a and b are not equal
     * @param personAContcatId a person's contact Id
     * @param personBContcatId a person's contact Id
     * @returns {boolean}
     */
    public static isNotTheSamePerson(personAContcatId: string, personBContcatId: string): boolean {
        if (IsNullOrUndefined(personAContcatId) || IsNullOrUndefined(personBContcatId)) {
            return false;
        }

        return personAContcatId !== personBContcatId;
    }

    public toPersonDropdownItem(isActive: boolean = false): PersonDropdownItem {
        let personDropdownItem = new PersonDropdownItem();
        personDropdownItem.Item = this;
        personDropdownItem.Active = isActive;
        return personDropdownItem;
    }

    public GetFormattedAge(now: Date = null): string {
        return this.DateofBirth
            ? MyleHelper.GetFormattedAge(this.DateofBirth, now)
            : "";
    }

    public GetFormattedDateOfBirth(): string {
        return this.DateofBirth
            ? new DateOnly(this.DateofBirth).toString()
            : "";
    }

    public GetFormattedPhonesByPriorityWithPosition(index: number, format: string = "+{0} ({1}) {2} {3} {4}", formatExtension: string = " Ext.{Ext}"): string {
        let phones = this.GetPhonesByPriority();
        if (phones.length > index)
            return phones[index].format(format, formatExtension);
        return "";
    }

    public GetPhonesByPriority(): PhoneNumber[] {
        return PhoneNumber.GetPhonesByPriority(this.Phones);
    }

    public GetFormattedGender(): string {
        let gender = "";
        if (this.GenderLookupId != null)
            gender = cacheStore.GetLocalizedValueByLookupId(this.GenderLookupId);

        return gender;
    }

    public Gender(): Gender {
        return MyleHelper.genderLookupToEnum(this.GenderLookupId);
    }

    public GetFormattedPreferredPronouns(): string {
        if (this.SpecifiedPreferredPronouns != null)
            return this.SpecifiedPreferredPronouns;

        if (this.PreferredPronouns != null && this.PreferredPronouns != PreferredPronouns.Unknown)
            return cacheStore.GetString(`Enums_PreferredPronouns_${PreferredPronouns[this.PreferredPronouns]}`);

        return "";
    }

    public static IsRegisteredToPortal(person: Person): boolean {
        let syncState = person.PortalSyncState;
        return syncState != null
            && (
                syncState == PortalRegistrationState.RegistrationComplete
                || syncState == PortalRegistrationState.RegistrationPending
                || syncState == PortalRegistrationState.ChildAccount
            );
    }
}

enum PersonSourceContext {
    IsIntervenorOnFile = 1 << 0,
    IsRamqIntervenor = 1 << 1,
    HasLastMedicalNote = 1 << 2
}

export { Person, PersonSourceContext };
